import {combineLatest} from 'rxjs';
import {distinctUntilChanged, filter, map, withLatestFrom} from 'rxjs/operators';
import {toWsMsg$, wsMsg$} from '@/ws';
import {isUtaSpot$} from '@/user/user';
import {wsLogin$} from '@/user/auth-ws';
import {mainUserId$} from '@/raw/users';
import {findMaoUID} from '@/raw/utils';
import type {ws} from '@/types';
type MAOOriginMsg = ws.MAOOriginMsg;
type MAOMsg = ws.MAOMsg;
type MarginOrderRaw = ws.MarginOrderRaw;

combineLatest([isUtaSpot$, wsLogin$])
  .pipe(filter(([isUtaSpot]) => !isUtaSpot))
  .subscribe(() => {
    toWsMsg$.next({method: 'mao.subscribe', params: []});
    isUtaSpot$.pipe(distinctUntilChanged(), filter(Boolean)).subscribe(() => {
      // maos 后端开发定义问题
      toWsMsg$.next({method: 'maos.unsubscribe', params: []});
    });
  });

/**
 * wallets_mao:
 * @typedef {object} WalletsMao
 * @property {string} balanceRq // 钱包余额
 * @property {string} currency // 币种
 * @property {number} lastUpdateTimeNs // 最近一次更新时间，单位纳秒
 * @property {string} lockedTradingBalanceRq // 因交易被锁定的数量
 * @property {string} lockedWithdrawRq // 因取款被锁定的数量
 * @property {string} borrowedRq // 借款的数量
 * @property {string} cumInterestRq // 累计未付的利息
 * @property {string} hourlyInterestRateRr // 最新的每小时利率
 * @property {string} maxAmountToBorrowRq // 该用户最大可借额度上限
 * @property {number} userID // 用户UID
 */
/**
 * orders_mao:
 * @typedef {object} OrdersMao
 * @property {string} averageRp -> averageEp
 * @property {string} baseQtyRq -> baseQtyEv
 * @property {string} cumBaseQtyRq -> cumBaseQtyEv
 * @property {string} cumFeeRv -> cumFeeEv
 * @property {string} cumQuoteQtyRq -> cumQuoteQtyEv
 * @property {string} curBaseWalletQtyRq -> curBaseWalletQtyEv
 * @property {string} curQuoteWalletQtyRq -> curQuoteWalletQtyEv
 * @property {string} leavesBaseQtyRq -> leavesBaseQtyEv
 * @property {string} leavesQuoteQtyRq -> leavesQuoteQtyEv
 * @property {string} pegOffsetValueRv -> pegOffsetValueEv
 * @property {string} priceRp -> priceEp
 * @property {string} quoteQtyRq -> quoteQtyEv
 * @property {string} stopPxRp -> stopPxEp
 * @property {string} borrowQtyRq
 * @property {string} paybackPrincipalQtyRq
 * @property {string} paybackInterestQtyRq
 * @property {string} hourlyInterestRateRr
 * @property {string} riskLevelRr
 * @property {string} liqFeeRv
 * @property {string} liqFeeRateRr
 */

export const maoMsg$ = combineLatest({isUtaSpot: isUtaSpot$, wsMsg: wsMsg$}).pipe(
  filter(
    ({isUtaSpot, wsMsg}: {isUtaSpot: boolean; wsMsg: MAOOriginMsg}) =>
      !isUtaSpot && !!wsMsg.wallets_mao && !!wsMsg.orders_mao
  ),
  map(({wsMsg}: {wsMsg: MAOOriginMsg}): MAOMsg => convertMaoMsg(wsMsg)),
  withLatestFrom(mainUserId$),
  filter(([wsMsg, mainUserId]) => findMaoUID(wsMsg) === mainUserId),
  map(([wsMsg]) => wsMsg)
);

function convertMaoMsg({orders_mao, wallets_mao, riskLevelRr, ...data}: MAOOriginMsg): MAOMsg {
  const {closed = [], open = [], fills = []} = orders_mao;
  const riskLevelEr = riskLevelRr;
  return {
    ...data,
    riskLevelEr,
    wallets: convertWalletsMao(wallets_mao, riskLevelEr),
    orders: {
      closed: convertOrders(closed) as ws.ClosedMarginOrderRaw[],
      open: convertOrders(open) as ws.OpenMarginOrderRaw[],
      fills: convertOrders(fills) as ws.FillMarginOrderRaw[],
    },
  };
}

function convertWalletsMao(wallets, riskLevelEr): ws.MarginAccountRaw[] {
  return wallets.map((o) => ({
    balanceEv: o.balanceRq || '0',
    fundEv: o.fundRq || '0',
    currency: o.currency,
    lastUpdateTimeNs: o.lastUpdateTimeNs,
    lockedTradingBalanceEv: o.lockedTradingBalanceRq || '0',
    lockedWithdrawEv: o.lockedWithdrawRq || '0',
    borrowedEv: o.borrowedRq || '0',
    cumInterestEv: o.cumInterestRq || '0',
    hourlyInterestRateEr: o.hourlyInterestRateRr,
    maxAmountToBorrowEv: o.maxAmountToBorrowRq || '0',
    riskLevelEr,
    userID: o.userID,
  }));
}

function convertOrders(orders): MarginOrderRaw[] {
  return orders.map((o) => {
    const {
      avgPriceRp,
      averageRp,
      baseQtyRq,
      cumBaseQtyRq,
      cumFeeRv,
      cumQuoteQtyRq,
      curBaseWalletQtyRq,
      curQuoteWalletQtyRq,
      execBaseQtyRq,
      execFeeRv,
      execPriceRp,
      execQuoteQtyRq,
      leavesBaseQtyRq,
      leavesQuoteQtyRq,
      pegOffsetValueRv,
      priceRp,
      quoteQtyRq,
      stopPxRp,
      borrowQtyRq,
      paybackPrincipalQtyRq,
      paybackInterestQtyRq,
      hourlyInterestRateRr,
      riskLevelRr,
      liqFeeRv,
      liqFeeRateRr,
      ...order
    } = o;
    return {
      ...order,
      avgPriceEp: avgPriceRp || '0',
      averageEp: averageRp || '0',
      baseQtyEv: baseQtyRq || '0',
      cumBaseQtyEv: cumBaseQtyRq || '0',
      cumFeeEv: cumFeeRv || '0',
      cumQuoteQtyEv: cumQuoteQtyRq || '0',
      curBaseWalletQtyEv: curBaseWalletQtyRq || '0',
      curQuoteWalletQtyEv: curQuoteWalletQtyRq || '0',
      execBaseQtyEv: execBaseQtyRq || '0',
      execFeeEv: execFeeRv || '0',
      execPriceEp: execPriceRp || '0',
      execQuoteQtyEv: execQuoteQtyRq || '0',
      leavesBaseQtyEv: leavesBaseQtyRq || '0',
      leavesQuoteQtyEv: leavesQuoteQtyRq || '0',
      pegOffsetValueEv: pegOffsetValueRv || '0',
      priceEp: priceRp || '0',
      quoteQtyEv: quoteQtyRq || '0',
      stopPxEp: stopPxRp || '0',
      borrowQtyEq: borrowQtyRq || '0',
      paybackPrincipalQtyEq: paybackPrincipalQtyRq || '0',
      paybackInterestQtyEq: paybackInterestQtyRq || '0',
      hourlyInterestRateEr: hourlyInterestRateRr || '0',
      riskLevelEr: riskLevelRr || '0',
      liqFeeEv: liqFeeRv || '0',
      liqFeeRateEr: liqFeeRateRr || '0',
    };
  });
}
